import { toPng } from 'html-to-image';
import type { ChartRect } from 'components/IncidentConfig/roster/rosterConnectorRouting';

/**
 * Pixel size of the capture. The ratio matches block 4 of ICS_207_CG.PDF
 * (749.6x412.2pt) so the image fills that box without letterboxing, and the
 * absolute size is large enough that names and emails stay legible once the PDF
 * scales it down.
 */
const CAPTURE_WIDTH = 2400;
const CAPTURE_HEIGHT = 1320;

/**
 * Matches the chart's own minZoom, so a large org chart fits rather than being
 * cropped.
 */
const MIN_ZOOM = 0.15;
const MAX_ZOOM = 2;
const FIT_PADDING = 0.1;

const FALLBACK_BACKGROUND = '#0b0f19';

export type ChartBounds = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/** Smallest box around every seat on the chart. */
export function boundsOfRects(rects: readonly ChartRect[]): ChartBounds | null {
  if (rects.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const rect of rects) {
    minX = Math.min(minX, rect.x);
    minY = Math.min(minY, rect.y);
    maxX = Math.max(maxX, rect.x + rect.width);
    maxY = Math.max(maxY, rect.y + rect.height);
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/**
 * The translate-and-scale that fits `bounds` into a viewport of the given size,
 * centred, with `padding` of the viewport left clear around it.
 */
export function viewportForBounds(
  bounds: ChartBounds,
  width: number,
  height: number,
  minZoom: number,
  maxZoom: number,
  padding: number
): { x: number; y: number; zoom: number } {
  const usableWidth = width * (1 - padding);
  const usableHeight = height * (1 - padding);
  const fitZoom = Math.min(
    usableWidth / Math.max(bounds.width, 1),
    usableHeight / Math.max(bounds.height, 1)
  );
  const zoom = Math.min(maxZoom, Math.max(minZoom, fitZoom));
  const centerX = bounds.x + bounds.width / 2;
  const centerY = bounds.y + bounds.height / 2;
  return {
    x: width / 2 - centerX * zoom,
    y: height / 2 - centerY * zoom,
    zoom,
  };
}

/**
 * Renders the org chart to a PNG sized for the ICS-207 form.
 *
 * The live viewport is not captured directly: it is clipped to a fixed-height
 * container and carries whatever pan and zoom the user left it at. Instead the
 * transform that would fit every node is computed and applied only for the
 * duration of the capture, so the export always contains the whole chart.
 */
export async function captureOrgChartImage(
  rects: readonly ChartRect[],
  container: HTMLElement | null,
  viewportEl: HTMLElement | null
): Promise<File | null> {
  const bounds = boundsOfRects(rects);
  if (!bounds || !container || !viewportEl) return null;

  const { x, y, zoom } = viewportForBounds(
    bounds,
    CAPTURE_WIDTH,
    CAPTURE_HEIGHT,
    MIN_ZOOM,
    MAX_ZOOM,
    FIT_PADDING
  );

  // Match the live canvas (dark or light) so seat text stays legible in the PDF.
  const styles = getComputedStyle(container);
  const isLight = container.closest('.light') != null;
  const background =
    (isLight
      ? styles.getPropertyValue('--background').trim() ||
        styles.getPropertyValue('--neutral-50').trim()
      : styles.getPropertyValue('--neutral-900').trim()) ||
    (isLight ? '#ffffff' : FALLBACK_BACKGROUND);

  const dataUrl = await toPng(viewportEl, {
    backgroundColor: background,
    width: CAPTURE_WIDTH,
    height: CAPTURE_HEIGHT,
    pixelRatio: 1,
    style: {
      width: `${CAPTURE_WIDTH}px`,
      height: `${CAPTURE_HEIGHT}px`,
      transform: `translate(${x}px, ${y}px) scale(${zoom})`,
    },
  });

  const blob = await (await fetch(dataUrl)).blob();
  return new File([blob], 'orgChart.png', { type: 'image/png' });
}
